import { formValueSelector } from 'redux-form';

export type TimelineValue = Mismo.PropertySearchType | Mismo.LoanPurposeType;

export const FORM_NAME = 'onboarding';

// set these configs because the onboarding form spans multiple pages
// https://redux-form.com/6.6.3/examples/wizard/
export const FORM_CONFIG = {
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
  form: FORM_NAME,
};

export const FIELDS = {
  cashDownPayment: 'cashDownPayment',
  estimatedCreditScore: 'estimatedCreditScore',
  loanType: 'loanType',
  loanPurpose: 'loanPurpose',
  phoneNumber: 'phoneNumber',
  propertyCounty: 'propertyCounty',
  propertySearchType: 'propertySearchType',
  purchasePrice: 'purchasePrice',
  propertyType: 'propertyType',
  propertyUsageType: 'propertyUsageType',
  loanAmount: 'loanAmount',
  propertyValue: 'propertyValue',
  refiPurpose: 'refiPurpose',
  veteranStatus: 'veteranStatus',
  userViewedTermsAndConditionsText: 'userViewedTermsAndConditionsText',
  // this is a dummy field that is the union of loanPurpose & propertySearchType for use on the timeline page
  _timeline: 'timeline',
};

export type CreditScore = '740' | '720' | '680' | '640' | '619';
export const CREDIT_SCORE_CHOICES = [
  {
    value: 740,
    text: 'Excellent (740 and above)',
  },
  {
    value: 720,
    text: 'Good (700-739)',
  },
  {
    value: 680,
    text: 'Average (660-699)',
  },
  {
    value: 640,
    text: 'Fair (620-659)',
  },
  {
    value: 619,
    text: 'Poor (below 620)',
  },
];

export const selector = formValueSelector<ReduxState>(FORM_NAME);

export function getInitialLoanType(state: ReduxState) {
  return state.onboarding.loanType;
}

export function getLoanType(state: ReduxState) {
  // loanType might not exist in the form if there was a loanType set when /signup was hit
  // or if there's only one loanType available
  const initalLoanType = getInitialLoanType(state);
  const selectedLoanType = selector(state, FIELDS.loanType);
  return selectedLoanType || initalLoanType;
}

export function getPropertySearchType(state: ReduxState) {
  return selector(state, FIELDS.propertySearchType);
}

export function getLoanPurpose(state: ReduxState) {
  return selector(state, FIELDS.loanPurpose);
}

export function getTimelineValue(state: ReduxState) {
  return selector(state, FIELDS._timeline);
}

export function getValueFromKey(state: ReduxState, key: string) {
  return selector(state, FIELDS[key]);
}

// Translates a timeline value into loanPurpose and propertySearchType.
// The REFINANCE value is a loanPurpose, and refinances have no propertySearchType value;
// All other values are propertySearchTypes that also implicitly map out to PURCHASE
export function translateTimelineValueToFormProps(value: TimelineValue) {
  if (value === 'REFINANCE') {
    return {
      [FIELDS.loanPurpose]: 'REFINANCE',
      [FIELDS.propertySearchType]: null,
      [FIELDS._timeline]: value,
    };
  } else {
    return {
      [FIELDS.loanPurpose]: 'PURCHASE',
      [FIELDS.propertySearchType]: value,
      [FIELDS._timeline]: value,
    };
  }
}
